import type { Feeder, Substation, Tenant } from "@/lib/api";
import type { SearchableOption } from "@/components/searchable-selector";
import { feederPickerOption } from "@/components/feeder-picker-option";
import { substationPickerOption } from "@/components/substation-picker-option";

/** Sort by the human name, then the unchanged catalog identity. */
function sorted<T>(rows: readonly T[], name: (row: T) => string, id: (row: T) => number): T[] {
  return [...rows].sort((a, b) =>
    name(a).localeCompare(name(b), undefined, { sensitivity: "base" }) || id(a) - id(b),
  );
}

export function tenantPickerOptions(rows: readonly Tenant[]): SearchableOption[] {
  return sorted(rows, (row) => row.name, (row) => row.tenant_id).map((row) => ({
    value: String(row.tenant_id),
    label: rows.filter((other) => other.name === row.name).length > 1
      ? `${row.name} · ${row.external_key} · ID ${row.tenant_id}` : row.name,
    searchText: `${row.external_key} ${row.kind}`,
  }));
}

export function substationPickerOptions(rows: readonly Substation[]): SearchableOption[] {
  return sorted(rows, (row) => row.name, (row) => row.substation_id)
    .map((row) => substationPickerOption(row, rows));
}

export function provisioningFeederOptions(rows: readonly Feeder[]): SearchableOption[] {
  return sorted(rows, (row) => row.feeder_name, (row) => row.feeder_id).map((row) => {
    const option = feederPickerOption(row);
    return {
      ...option,
      label: rows.filter((other) => other.feeder_name === row.feeder_name).length > 1
        ? `${option.label} · ${row.external_key} · ID ${row.feeder_id}` : option.label,
      searchText: `${option.searchText ?? ""} ${row.distribution_company_name}`,
    };
  });
}
